import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import ReportCard from 'component/reports/ReportCard';
import { reportService } from '../../services/reportService';
import { translateSectorLabel } from "utils/sectorLabel";

const normalizeSectors = (value) => {
  const input = Array.isArray(value) ? value : (value ? [value] : []);
  return input
    .map((sector) => {
      if (typeof sector === 'string') return sector;
      if (sector && typeof sector === 'object') return sector.name || sector.slug || '';
      return '';
    })
    .filter(Boolean);
};

const mapReport = (r = {}, t) => ({
  id: r.id ?? r.report_id ?? r._id,
  title: r.title || r.name || t("reports.untitled"),
  summary: r.summary || r.description || '',
  image_url: r.hero_image_url || r.image_url || r.cover_image,
  match_rate: r.match_rate ?? r.score ?? null,
  sectors: normalizeSectors(r.sectors || (r.sector ? [r.sector] : [])),
  recent_at: r.created_at || r.published_at || r.updated_at,
  reading_time_minutes: r.reading_time_minutes || r.read_time_minutes || r.reading_minutes || null,
});

const MatchingReports = () => {
  const { t, i18n } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await reportService.listReports({ limit: 12 });
        const arr = Array.isArray(data) ? data : [];
        setReports(arr.map((item) => mapReport(item, t)));
      } catch (e) {
        setError(e?.message || t("reports.error.load"));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [t]);

  const topReport = useMemo(() => reports[0], [reports]);
  const others = useMemo(() => reports.slice(1), [reports]);

  return (
    <div className='main__content__box'>
      <div className="container-fluid">
        <div className="row g-4">
          <div className="col-md-8">
            <div className="latest_report_wrap">
              <div className="bg_glassEffect p-4 rounded-4 text-white mb-4">
                {loading && <div className="text-muted mb-2">{t("reports.loading")}</div>}
                {error && <div className="text-danger mb-2">{error}</div>}
                {topReport ? (
                  <>
                    <h5 className="fw-semibold mb-3 text-white">
                      {topReport.title}
                    </h5>
                    <p className="text-light mb-0">
                      {topReport.summary}
                    </p>
                  </>
                ) : (
                  <p className="text-light mb-0">{t("reports.matching.noReport")}</p>
                )}
              </div>
            </div>
            <div className='content_wrap'>
              <h4 className='text-white fw-semibold mb-4 text-uppercase'>{t("reports.matching.title")}</h4>
              <div className="row g-4">
                {others.map((report, index) => (
                  <div className="col-md-6 col-lg-6" key={report.id || index}>
                    <ReportCard
                      value={index + 1}
                      title={report.title}
                      summary={report.summary}
                      _id={report.id}
                      progress={report.match_rate}
                      image_url={report.image_url}
                      sectors={report.sectors}
                      created_at={report.recent_at}
                      reading_time_minutes={report.reading_time_minutes}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-4">

            <div className="matching_sector_tabs_box  ">
              <div className="bg_glassEffect pt-4 pb-3 px-3 rounded-5">
                <ul className="news-tabs" role="tablist">
                  <li className="news-tab-item" role="presentation">
                    <button
                      className="news-tab-btn active fw-semibold text-uppercase"
                      id="feed-tab"
                      type="button"
                    >
                      {t("reports.allSectors")}
                    </button>
                  </li>

                  <li className="news-tab-item" role="presentation">
                    <button
                      className="news-tab-btn fw-semibold text-uppercase"
                      id="all-tab"
                      type="button"
                    >
                      {t("reports.allSources")}
                    </button>
                  </li>
                </ul>

              </div>
            </div>

            <div className="bg_glassEffect p-3 rounded-5 mt-4">
              <h6 className="text-white">{t("reports.matching.topSectors")}</h6>
              <div className="d-flex flex-wrap gap-2">
                {topReport?.sectors?.length
                  ? topReport.sectors.map((s, i) => (
                      <span key={i} className="badge bg-secondary">{translateSectorLabel(typeof s === "string" ? s : (s?.name || s?.slug || ""), i18n.resolvedLanguage || i18n.language)}</span>
                    ))
                  : <span className="text-muted">{t("reports.matching.noSectorTags")}</span>}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingReports;
